import { type WebConfig } from '@koala/sdk';

/** Has the brand chosen Koala IDs and Brand (Olo/Chowly) IDs for their Location URLs. */
export function supportsBrandId(config: WebConfig) {
  return config.locations.location_id_source === 'brand_id';
}

/**
 * `seo.noindex` does not exist in the generated `WebConfig` yet. It originates in the
 * Configurations API's `web-config` JSON Schema, and everything under
 * `packages/sdk/src/types/gen/` is regenerated from that schema by `yarn schema:sync` —
 * so the field cannot be added from this repo, and hand-editing the generated files
 * would be silently reverted by the next sync.
 *
 * Reading it through a local narrowing keeps this correct on both sides of that change:
 * `getWebConfig` casts the API payload straight to `WebConfig` without validating or
 * stripping unknown keys (`packages/sdk/src/v4/api/configurations.service.ts`), so the
 * value is live the moment the config carries it, and once the field does land in the
 * generated type the intersection still type-checks and this alias can simply be deleted.
 *
 * @see https://chowly.atlassian.net/browse/TEAM-14137
 */
type SeoWithNoindex = WebConfig['seo'] & { noindex?: boolean | null };

/**
 * Has the brand opted its ordering pages out of search indexing, so they stop competing
 * with the canonical marketing `/location/<slug>` pages?
 *
 * Deliberately an identity check rather than a truthiness check: this schema spells
 * "unset" as `null` (see `seo.robots_txt`, `seo.store_page_title`), so an absent field, a
 * `null` and an explicit `false` all have to mean off. Off is the default — brands opt in.
 */
export function shouldNoindexOrderingPages(config: WebConfig) {
  return (config.seo as SeoWithNoindex).noindex === true;
}
